import {
  Bottomsheet,
  Button,
  Description,
  DetailItemD,
  Divider,
  HeadTitle,
  ItemContent,
  Spacer,
  Tmd14,
  Tmd18,
  Trg12,
} from 'oui';
import { useMtgDetail } from '../../../application/bloc/useMtgDetail';
import { addUnitToMaxAmount } from '../../../application/helper/filter';
import { ClkBtnSubEventNm } from '../../../application/hooks/useBeacon';
import useNav from '../../../application/hooks/useNav';
import useRateCalcBottomSheet from '../../../application/hooks/useRateCalcBottomSheet';
import InfoTable from '../../dataDisplay/InfoTable/InfoTable';
import { AccordionList } from '../../etc/Accordion/AccordionList';
import { PrefRateContent } from '../../etc/Accordion/PrefRateContent';
import {
  AlignBox,
  CenterBox,
  DefaultBox,
  GridBox2,
  SideBox,
} from '../../etc/LBox/LBox';
import { StepBox } from '../../etc/Step/StepBox';
import RateCalcBottomSheet from '../../feedback/RateCalcBottomSheet/RateCalcBottomSheet';
import { useEffect, useRef } from 'react';
import { useBaseParam } from '../../../application/hooks/useBaseParam';
import { mutateHouseLoanExecute } from '../../../infra/api/mtg/mutateHouseLoanExecute';
import {
  RATE_CALC_RESULT_BOTTOMSHEET_BANNER,
  RATE_CALC_RESULT_PAGE_BANNER,
} from '../../../application/staticData/Banner/RATE_CALC_RESULT_BANNER';

interface IProductInfoSheetProps {
  productOptions: any[];
  infoText?: string;
}

const ProductInfoSheet = ({
  productOptions,
  infoText,
}: IProductInfoSheetProps) => {
  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      if (targetRef.current) {
        const top = targetRef.current?.offsetTop ?? 0;
        targetRef.current.style.cssText = `overflow-y: scroll; overflow-x: hidden; max-height: calc(90vh - ${top}px);`;
      }
    }, 300);
  }, []);

  return (
    <div ref={targetRef}>
      <Description
        backgroundColor={'white'}
        productOptions={productOptions}
        infoText={infoText ?? ''}
      />
    </div>
  );
};

export const MtgDetail = () => {
  const {
    productInfo,
    custPreProductInfo,
    custRequestProductInfo,
    isLoanComplete,
    hashTagList,
    btnText,
    primeRate,
    calcRate,
    calcInterest,
    calcAmount,
    stepList,
    doc,
    mtgInfo,
    productOptionsSummary,
    productOptionsDetail,
    bottomsheet,
    openBottomsheet,
    navigateCallTime,
    navigateComplete,
    sendBeaconClkBtn,
    onClickPrimeRate,
  } = useMtgDetail();
  const { navigateByLinkType } = useNav();

  const executeLoanByLink = useBaseParam(mutateHouseLoanExecute, {
    preRequestDtlNo: custPreProductInfo?.preRequestDtlNo,
  });

  const putExecuteLoan = async () => {
    await executeLoanByLink();
  };

  const onClickExecuteLoanOnCalcuator = async () => {
    sendBeaconClkBtn(
      ClkBtnSubEventNm.LoanApp,
      RATE_CALC_RESULT_BOTTOMSHEET_BANNER[0].primaryText,
    );
    executeLoan();
  };

  const handleClickCta = async (e: any) => {
    sendBeaconClkBtn(ClkBtnSubEventNm.LoanApp, e.target.innerHTML);
    executeLoan();
  };

  const executeLoan = async () => {
    if (
      productInfo.applicationMethod === 'CALL' ||
      productInfo.applicationMethod === 'CALL_LINK'
    ) {
      navigateCallTime({
        preRequestDtlNo: custPreProductInfo.preRequestDtlNo,
        compNm: productInfo.compNm,
        callNo: productInfo.applicationCallNumber,
        link: productInfo?.applicationRedirectionUrl,
      });
    } else {
      //  신청 api 호출
      await putExecuteLoan();
      navigateByLinkType(
        productInfo?.applicationMethod?.toLowerCase(),
        productInfo?.applicationRedirectionUrl,
      );
      navigateComplete();
    }
  };

  const handleClickDocInfo = () => {
    sendBeaconClkBtn('', '서류 제출하기 도움말');
    openBottomsheet('대출 신청을 위해\n 꼭 필요한 서류들이에요');
  };

  const { isBottomSheetOpen, onClickOpenBottomSheet, onClickCloseBottomSheet } =
    useRateCalcBottomSheet();

  const handleClickCalcRate = (e?: any) => {
    sendBeaconClkBtn('', e.target.innerHTML || '다른 조건으로 이자 계산하기');
    onClickOpenBottomSheet();
  };

  const handleClickCloseBottomSheet = () => {
    onClickCloseBottomSheet();
  };

  const sheet1 = (
    <p
      style={{ padding: '20px', lineHeight: '30px' }}
      dangerouslySetInnerHTML={{ __html: doc.replace(/\n/g, '<br />') }}
    />
  );

  // 상품 상세 정보
  const sheet2 = (
    <ProductInfoSheet
      productOptions={productOptionsDetail}
      infoText={productInfo?.infoText}
    />
  );
  // 담보 정보
  const sheet3 = <InfoTable informs={mtgInfo} direction={'columns'} />;

  // 요약 정보
  const sheet4 = (
    <InfoTable
      onClick={(val) => val && openBottomsheet('상품 안내')}
      informs={productOptionsSummary}
      direction={'row'}
      type={'thin'}
    />
  );

  // if (!productInfo) {
  //   return <></>;
  // }

  return (
    <>
      <DetailItemD
        avatar={{
          source: productInfo?.logoImg,
          alt: productInfo?.compNm ?? 'logo',
        }}
        textTitle={productInfo?.compNm}
        subTitle={productInfo?.productNm}
        eventChip={hashTagList}
        labelA={'예상 금리'}
        valueA={`${
          isLoanComplete
            ? custRequestProductInfo?.interestRate
            : custPreProductInfo?.interestRate
        }%`}
        labelB={'예상 최대 한도'}
        valueB={addUnitToMaxAmount(
          isLoanComplete
            ? custRequestProductInfo?.amount
            : custPreProductInfo?.amount,
        )}
        iconRight={{
          name: 'ChevronRightSharp',
          size: 'sm',
        }}
        textButton={{
          text: '담보 확인하기',
          size: 'sm',
          state: 'enabled',
          onClickButton: () => openBottomsheet('담보 정보'),
        }}
      />
      <CenterBox>
        <Button
          flexible={true}
          configuration={'contained'}
          children={btnText}
          state={
            productInfo?.ctaInactiveYn === 'Y' &&
            productInfo?.ctaTextInactive === btnText
              ? 'disabled'
              : 'enabled'
          }
          isDisabled={
            productInfo?.ctaInactiveYn === 'Y' &&
            productInfo?.ctaTextInactive === btnText
          }
          onClick={(e) => handleClickCta(e)}
        />
      </CenterBox>

      {primeRate.hasPrimeRate && (
        <AccordionList
          backgroundColor={'white'}
          list={[
            {
              title: (
                <>
                  <Tmd18 text={'우대금리'} />
                  <br />
                  <Tmd14
                    text={`금리가 ${primeRate.maxPrimeRate}% 낮아져요`}
                    color={'#FF3A4A'}
                  />
                </>
              ),
              content: (
                <PrefRateContent
                  list={primeRate.prefRate}
                  onClick={onClickPrimeRate}
                />
              ),
            },
          ]}
        />
      )}

      <Divider type={'content'} />
      <HeadTitle textTitle={'이자 계산 결과'} />
      <Spacer size={'xs'} />
      <SideBox>
        {calcRate.slice(0, 3).map((item, idx) => {
          return (
            <div key={`key_${idx}`}>
              <ItemContent
                stress={false}
                textTitle={item.text}
                subTitle={item.label}
              />
              <Spacer size={'md'} />
            </div>
          );
        })}
        <GridBox2 repeat={4}>
          {calcRate.slice(3, 7).map((item, idx) => {
            return (
              <div key={item.label}>
                <Trg12 text={item.label} color={'#9C9C9C'} />
                <br />
                <Tmd14 text={item.text} />
              </div>
            );
          })}
        </GridBox2>
      </SideBox>

      <CenterBox>
        <Button
          onClick={(e) => handleClickCalcRate(e)}
          flexible={false}
          size={'sm'}
          children={'다른 조건으로 이자 계산하기'}
          configuration={'outlined'}
          color={'default'}
        />
      </CenterBox>
      <Divider type={'content'} />

      <HeadTitle
        textTitle={'대출 진행 과정'}
        subTitle={'아래와 같은 순서로 주택담보대출이 진행돼요'}
      />
      <StepBox list={stepList} onClick={handleClickDocInfo} />
      <Spacer size={'md'} />
      <Divider type={'content'} />

      <HeadTitle textTitle={'대출 상품 요약'} />
      {sheet4}
      <Spacer size={'lg'} />

      <Bottomsheet
        backdrop={true}
        isOpen={bottomsheet.isOpen}
        isMaxHeight={false}
        children={
          <>
            <HeadTitle
              textTitle={bottomsheet.title}
              buttonIconName={'CloseSharp'}
              onClickButton={bottomsheet.close}
            />
            <div style={{ overflow: 'scroll', height: '100%' }}>
              {bottomsheet.title === '상품 안내'
                ? sheet2
                : bottomsheet.title === '담보 정보'
                ? sheet3
                : sheet1}
            </div>
            <Spacer size={'md'} />
          </>
        }
      />

      <RateCalcBottomSheet
        isShow={isBottomSheetOpen}
        handleClickOpenBottomSheet={handleClickCalcRate}
        handleClickCloseBottomSheet={handleClickCloseBottomSheet}
        preset={{
          amount: (calcAmount / 10000).toString(),
          interestRate: calcInterest.toString(),
        }}
      />
    </>
  );
};
